/**
 * Widget "Sentiment × Tier IA" — corrélation entre sentiment d'appel et scoring IA.
 *
 * Affiche :
 *   - Matrice 3×3 sentiment × tier (heatmap colorée)
 *   - KPIs (calls analysés, alignement, anomalies)
 *   - Insights actionnables (success/warning/anomaly/alert)
 *
 * Endpoints :
 *   GET /api/crm/coaching/sentiment-tier-correlation
 */
import { useEffect, useMemo, useState } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { Loader2, AlertTriangle, CheckCircle2, Search, Megaphone } from "lucide-react";

const SENT_LABEL = { positif: "😊 Positif", neutre: "😐 Neutre", negatif: "😟 Négatif" };
const TIER_LABEL = { hot: "🔥 HOT", warm: "🌤️ WARM", cold: "❄️ COLD" };
const TIER_COLOR = { hot: "#f43f5e", warm: "#f59e0b", cold: "#3b82f6" };

const KIND_META = {
  success:  { icon: CheckCircle2,  bg: "bg-emerald-500/10",  border: "border-emerald-500/30",  text: "text-emerald-200" },
  warning:  { icon: AlertTriangle, bg: "bg-amber-500/10",    border: "border-amber-500/30",    text: "text-amber-200" },
  anomaly:  { icon: Search,        bg: "bg-violet-500/10",   border: "border-violet-500/30",   text: "text-violet-200" },
  alert:    { icon: Megaphone,     bg: "bg-rose-500/10",     border: "border-rose-500/30",     text: "text-rose-200" },
};

export default function SentimentTierCorrelationCard({ compact = false }) {
  const { axiosAuth } = useAuth();
  const api = useMemo(() => axiosAuth(), [axiosAuth]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const r = await api.get("/crm/coaching/sentiment-tier-correlation");
        setData(r.data);
      } catch {
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
    /* eslint-disable-next-line */
  }, []);

  if (loading) {
    return (
      <div className="bg-[#0b0811] rounded-xl border border-white/5 p-4 flex items-center gap-2 text-xs text-slate-400">
        <Loader2 className="w-3.5 h-3.5 animate-spin" /> Chargement de l'analyse de corrélation…
      </div>
    );
  }
  if (!data) return null;

  const { matrix, counts, insights, total_with_tier } = data;
  const max = Math.max(1, ...["positif", "neutre", "negatif"].flatMap((s) => ["hot", "warm", "cold"].map((t) => matrix[s][t])));

  return (
    <div className="bg-[#0b0811] rounded-2xl border border-violet-500/20 p-5 space-y-4" data-testid="sentiment-tier-card">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <h3 className="text-sm font-bold text-white inline-flex items-center gap-2">
            <Megaphone className="w-4 h-4 text-violet-400" /> Coaching IA — Sentiment × Tier
          </h3>
          <p className="text-[11px] text-slate-500 mt-0.5">
            Corrélation entre le ressenti des appels (Whisper+Gemini) et le scoring IA des prospects.
          </p>
        </div>
        <div className="text-right">
          <div className="text-[10px] uppercase tracking-wider text-slate-500">Appels analysés</div>
          <div className="text-lg font-bold text-white">{counts?.total_calls_analyzed || 0}</div>
        </div>
      </div>

      {total_with_tier === 0 ? (
        <div className="bg-[#14101c] rounded-lg p-4 text-center text-xs text-slate-500" data-testid="no-data">
          Pas encore assez de données pour calculer la corrélation.<br />
          Activez la transcription IA et le scoring IA pour démarrer l'analyse.
        </div>
      ) : (
        <>
          {/* Matrice 3x3 */}
          <div className="overflow-x-auto" data-testid="sentiment-tier-matrix">
            <table className="w-full text-xs border-separate border-spacing-1">
              <thead>
                <tr>
                  <th className="text-left text-[10px] uppercase text-slate-500 font-bold p-1">&nbsp;</th>
                  {["hot", "warm", "cold"].map((t) => (
                    <th key={t} className="text-[10px] uppercase font-bold p-1" style={{ color: TIER_COLOR[t] }}>
                      {TIER_LABEL[t]}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {["positif", "neutre", "negatif"].map((s) => (
                  <tr key={s}>
                    <td className="text-[11px] text-white font-bold p-2 whitespace-nowrap">{SENT_LABEL[s]}</td>
                    {["hot", "warm", "cold"].map((t) => {
                      const v = matrix[s][t];
                      const intensity = v / max;
                      const tierColor = TIER_COLOR[t];
                      // Highlight aligned cells
                      const aligned = (s === "positif" && t === "hot") || (s === "negatif" && t === "cold");
                      const conflict = (s === "positif" && t === "cold") || (s === "negatif" && t === "hot");
                      return (
                        <td key={t} className={`text-center p-3 rounded-lg font-bold ${aligned ? "ring-2 ring-emerald-500/40" : ""} ${conflict && v > 0 ? "ring-2 ring-rose-500/40" : ""}`} style={{ background: `${tierColor}${Math.round(intensity * 64).toString(16).padStart(2, "0")}`, color: intensity > 0.3 ? "#fff" : "#94a3b8" }} data-testid={`cell-${s}-${t}`}>
                          {v}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Insights */}
          {insights?.length > 0 && (
            <div className="space-y-2" data-testid="insights-list">
              {insights.map((ins, i) => {
                const meta = KIND_META[ins.kind] || KIND_META.warning;
                const Icon = meta.icon;
                return (
                  <div key={i} className={`${meta.bg} ${meta.border} border rounded-lg p-3 flex items-start gap-2`} data-testid={`insight-${i}`}>
                    <Icon className={`w-4 h-4 ${meta.text} flex-shrink-0 mt-0.5`} />
                    <div>
                      <div className={`text-xs font-bold ${meta.text}`}>{ins.title}</div>
                      <div className="text-[11px] text-slate-300 mt-0.5">{ins.message}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Footer */}
          {!compact && (
            <div className="text-[10px] text-slate-500 border-t border-white/5 pt-2">
              Cellules vertes = alignement attendu (positif↔HOT, négatif↔COLD).
              Cellules rouges = anomalies à investiguer.
            </div>
          )}
        </>
      )}
    </div>
  );
}
